// src/hooks/useTableExport.ts
import { useCallback } from 'react';
import { exportTableXLSX } from '@/lib/clientExport';

/**
 * useTableExport
 * Exports an array of row objects to CSV or a printable PDF page.
 *
 * Usage:
 *   const { exportCSV, exportPDF } = useTableExport(data, columns, 'capa-list');
 */
export interface ExportColumn {
  key: string;
  label: string;
}

export function useTableExport(
  data: Record<string, unknown>[],
  columns: ExportColumn[],
  filename: string
) {
  const exportCSV = useCallback(() => {
    if (!data?.length) return;
    const keys = columns.map((c) => c.key);
    const headers = columns.map((c) => c.label);
    const rows = [
      headers.join(','),
      ...data.map((row) =>
        keys
          .map((k) => {
            const v = row[k];
            if (v === null || v === undefined) return '';
            const s = String(v);
            return s.includes(',') || s.includes('"') || s.includes('\n')
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(',')
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.href = URL.createObjectURL(blob);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }, [data, columns, filename]);

  const exportPDF = useCallback(() => {
    if (!data?.length) return;
    const keys = columns.map((c) => c.key);
    const headers = columns.map((c) => c.label);

    // Build an HTML page and open in new tab for print-to-PDF
    const tableRows = data
      .map(
        (row) =>
          `<tr>${keys
            .map(
              (k) =>
                `<td style="border:1px solid #e2e8f0;padding:6px 10px;font-size:12px;color:#0f172a">${
                  row[k] !== null && row[k] !== undefined ? String(row[k]) : '—'
                }</td>`
            )
            .join('')}</tr>`
      )
      .join('');

    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>${filename}</title>
  <style>
    body { font-family: 'Segoe UI', sans-serif; padding: 24px; color: #0f172a; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    p  { font-size: 12px; color: #64748b; margin-bottom: 16px; }
    table { border-collapse: collapse; width: 100%; }
    th { background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 10px; font-size: 11px; text-align: left; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; }
    tr:nth-child(even) td { background: #f8fafc; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>${filename.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</h1>
  <p>Exported ${new Date().toLocaleString()} · ${data.length} records</p>
  <table>
    <thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  <script>window.onload = () => { window.print(); }</` + `script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onafterprint = () => URL.revokeObjectURL(url);
    }
  }, [data, columns, filename]);

  const exportXLSX = useCallback(() => {
    if (!data?.length) return;
    exportTableXLSX(filename, columns, data);
  }, [data, columns, filename]);

  return { exportCSV, exportPDF, exportXLSX };
}
